import { useEffect, useState } from "react";

// Tailwind's `md` breakpoint: below it the trading pages switch to their
// stacked mobile layout.
const QUERY = "(max-width: 767.98px)";

const read = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(QUERY).matches
    : false;

/**
 * Whether the viewport is phone-width.
 *
 * This used to be `window.innerWidth < 768`, checked once on mount and on
 * `resize`. In-app wallet browsers (MetaMask) and mobile Chrome can report the
 * 980px fallback layout width before the viewport meta tag applies, and don't
 * always fire `resize` once it does — so phones got the desktop two-column
 * layout with a clipped order ticket. A media query tracks the same viewport
 * CSS uses and notifies on every change, and the state starts from the real
 * value instead of `false`, so there's no desktop first paint either.
 */
export default function useIsMobile() {
  const [isMobile, setIsMobile] = useState(read);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mql = window.matchMedia(QUERY);
    const update = () => setIsMobile(mql.matches);
    update();

    if (mql.addEventListener) mql.addEventListener("change", update);
    else mql.addListener(update); // Safari < 14
    // Belt and braces for webviews that settle the viewport late.
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", update);
      else mql.removeListener(update);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  return isMobile;
}
